import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { eventoSchema, inscripcionSchema, primerError } from "./carrera.shared";
export const getEventoCarrera = createServerFn({ method: "GET" }).handler(async () => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
  const url = process.env["SUPABASE_URL"];
  if (!key || !url) throw new Error("No se pudo cargar el evento");
  const sb = createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`)
          h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
  const { data, error } = await sb
    .from("eventos_carrera")
    .select("*")
    .eq("id", "san-silvestre-2026")
    .single();
  if (error) throw new Error("No se pudo cargar el evento");
  return data;
});
export const crearInscripcionCarrera = createServerFn({ method: "POST" })
  .inputValidator((v) => {
    const r = inscripcionSchema.safeParse(v);
    if (!r.success) throw new Error(primerError(r.error));
    return r.data;
  })
  .handler(async ({ data }) => {
    const { crearInscripcion } = await import("./carrera.server");
    return crearInscripcion(data);
  });
export const resultadoCarrera = createServerFn({ method: "POST" })
  .inputValidator((v) => z.object({ token: z.string().uuid() }).parse(v))
  .handler(async ({ data }) => {
    const { leerResultado } = await import("./carrera.server");
    return leerResultado(data.token);
  });
export const reintentarCarrera = createServerFn({ method: "POST" })
  .inputValidator((v) => z.object({ token: z.string().uuid() }).parse(v))
  .handler(async ({ data }) => {
    const { reintentar } = await import("./carrera.server");
    return reintentar(data.token);
  });
export const gestionCarrera = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: admin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!admin) throw new Error("Acceso restringido");
    const [evento, rows] = await Promise.all([
      context.supabase.from("eventos_carrera").select("*").eq("id", "san-silvestre-2026").single(),
      context.supabase
        .from("inscripciones_carrera")
        .select(
          "id,contacto_nombre,email,telefono,participantes,total,estado,created_at,pagado_at,email_enviado_at",
        )
        .eq("evento_id", "san-silvestre-2026")
        .order("created_at", { ascending: false }),
    ]);
    if (evento.error || rows.error) throw new Error("No se pudieron cargar las inscripciones");
    return { evento: evento.data, rows: rows.data };
  });
export const guardarEventoCarrera = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v) => eventoSchema.parse(v))
  .handler(async ({ data, context }) => {
    const { data: admin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!admin) throw new Error("Acceso restringido");
    const { error } = await context.supabase
      .from("eventos_carrera")
      .update(data)
      .eq("id", "san-silvestre-2026");
    if (error) throw new Error("No se pudo guardar");
    return { ok: true };
  });

const tokenInput = (v: unknown) => z.object({ token: z.string().uuid("Código QR no válido") }).parse(v);
async function soloAdmin(context: { supabase: any; userId: string }) {
  const { data: admin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (!admin) throw new Error("Solo los administradores pueden validar inscripciones");
}
export const consultarQrCarrera = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(tokenInput)
  .handler(async ({ data, context }) => {
    await soloAdmin(context);
    const { data: r } = await context.supabase
      .from("inscripciones_carrera")
      .select("id,contacto_nombre,email,telefono,participantes,total,estado,validado_at")
      .eq("token", data.token)
      .maybeSingle();
    if (!r) throw new Error("Este código QR no corresponde a ninguna inscripción");
    return r;
  });
export const validarQrCarrera = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(tokenInput)
  .handler(async ({ data, context }) => {
    await soloAdmin(context);
    const { data: r, error } = await context.supabase.rpc("validar_inscripcion_carrera", { _token: data.token });
    if (error) throw new Error("No se pudo validar la inscripción");
    return (r as any[])[0] as { estado: string; validado_at: string | null; ya_validada: boolean };
  });

const dorsalesInput = (v: unknown) =>
  z
    .object({
      clave: z.string().regex(/^\d{4,12}$/, "Escribe la clave numérica"),
      token: z.union([z.string().uuid("Código QR no válido"), z.literal("")]),
      marcar: z.boolean().default(false),
    })
    .parse(v);
export const dorsalesCarrera = createServerFn({ method: "POST" })
  .inputValidator(dorsalesInput)
  .handler(async ({ data }) => {
    const { fichaDorsales } = await import("./carrera.server");
    return fichaDorsales(data.token, data.clave, data.marcar);
  });

export const confirmarPagoManualCarrera = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v) => z.object({ id: z.string().uuid() }).parse(v))
  .handler(async ({ data, context }) => {
    await soloAdmin(context);
    const { confirmarManualCarrera } = await import("./carrera.server");
    const r = await confirmarManualCarrera(data.id);
    if (!r.ok) throw new Error("Marcada como pagada, pero el correo no se pudo enviar");
    return r;
  });
export const reenviarCorreoCarrera = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((v) => z.object({ id: z.string().uuid() }).parse(v))
  .handler(async ({ data, context }) => {
    await soloAdmin(context);
    const { enviarCorreosCarrera } = await import("./carrera.server");
    if (!(await enviarCorreosCarrera(data.id, false))) throw new Error("No se pudo enviar el correo");
    return { ok: true };
  });
